<script setup>
import { useBlockSafe } from '@/composables/useBlockSafe';
import {
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
  computed,
} from 'vue';

import Gallery from '@/components/ui/Gallery.vue';
import Iframe from '@/components/ui/Iframe.vue';
const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});

const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  title,
  video,
  items,
  hasTitle,
  hasVideo,
  hasItems,
  hasAnyContent,
} = useBlockSafe(props.data);

const activeTab = ref(items.value?.[0]?.id || null);

const tabViewMode = ref({});

/** viewMode: if 3D — start with "gallery", else "gallery" as default */
const initViewModes = () => {
  const modes = {};
  for (const t of items.value || []) {
    const has3d = !!t?.threeD;
    modes[t.id] = 'gallery';
    if ((!t.gallery || !t.gallery.length) && has3d) {
      modes[t.id] = '3d';
    }
  }
  tabViewMode.value = modes;
};
initViewModes();

const currentTab = computed(
  () => (items.value || []).find((t) => t.id === activeTab.value) || null,
);

const currentMode = computed(
  () => tabViewMode.value[activeTab.value] || 'gallery',
);

const hasGallery = computed(() => !!currentTab.value?.gallery?.length);
const has3D = computed(() => !!currentTab.value?.threeD);

const toggleMode = async () => {
  if (!currentTab.value) return;
  const nextMode = currentMode.value === 'gallery' ? '3d' : 'gallery';
  if (nextMode === '3d' && !has3D.value) return;
  if (nextMode === 'gallery' && !hasGallery.value) return;

  tabViewMode.value = {
    ...tabViewMode.value,
    [activeTab.value]: nextMode,
  };

  await nextTick();
  window.dispatchEvent(new Event('resize'));
};

watch(activeTab, async () => {
  await nextTick();
  window.dispatchEvent(new Event('resize'));
});

const tabListElement = useTemplateRef('tabList');

const onTabListResize = () => {
  tabListElement.value?.updateInkBar();
};
onMounted(() => {
  window.addEventListener('resize', onTabListResize, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onTabListResize);
});

const scrollToVideo = () => {
  if (video.value) {
    if (typeof window === 'undefined') return;

    const el = document.getElementById(video.value);
    if (!el) return;

    const prefersReduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)',
    )?.matches;

    const top = el.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top,
      behavior: prefersReduced ? 'auto' : 'smooth',
    });

    el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
  }
};
</script>
<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      backgroundColor === 'black' ? 'white' : 'black',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div class="car-media">
        <h2
          v-if="hasTitle"
          class="mb-0 car-media__title h2 text-center"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="title"
        ></h2>
        <Tabs
          v-if="hasItems"
          v-model:value="activeTab"
          :scrollable="false"
          class="car-media__tabs"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
        >
          <TabList
            ref="tabList"
            :class="`car-media__tabs-headers tabs-bg-${backgroundColor}`"
          >
            <Tab
              v-for="tab in items"
              :key="tab.id"
              :value="tab.id"
              :class="[
                `car-media__tabs-title car-media__tabs-title--${backgroundColor}`,
              ]"
            >
              {{ tab.title }}
            </Tab>
          </TabList>
          <TabPanels class="car-media__tabs-content">
            <TabPanel
              v-for="tab in items"
              :key="tab.id"
              :value="tab.id"
              class="car-media__tab"
            >
              <div v-if="tabViewMode[tab.id] === 'gallery'">
                <Gallery
                  v-if="tab.gallery && tab.gallery.length > 0"
                  :id="`${id}-${tab.id}`"
                  :items="tab.gallery"
                  :has-mode-buttons="tab.threeD || hasVideo"
                  :title="title"
                  :background-color="backgroundColor"
                >
                  <template #mode-buttons>
                    <div
                      v-if="tab.threeD || hasVideo"
                      class="car-media__bottom car-media__bottom--gallery"
                    >
                      <Button
                        v-if="tab.threeD"
                        class="car-media__btn button-black js-button-car-3d"
                        rounded
                        @click="toggleMode"
                      >
                        {{ $t('car.view_in_3d') }}
                      </Button>
                      <Button
                        v-if="hasVideo"
                        class="car-media__btn button-transparent js-button-car-video"
                        rounded
                        :variant="'outlined'"
                        @click="scrollToVideo"
                      >
                        {{ $t('car.video_car_review') }}
                      </Button>
                    </div>
                  </template>
                </Gallery>
              </div>
              <div v-else-if="tabViewMode[tab.id] === '3d'">
                <Iframe
                  :ratio="'328:208'"
                  :url="tab.threeD"
                  title="3D Model"
                  allow="autoplay; fullscreen; xr-spatial-tracking"
                  :fullscreen="true"
                />
                <div v-if="tab.gallery || hasVideo" class="car-media__bottom">
                  <Button
                    v-if="tab?.gallery"
                    class="car-media__btn button-black js-button-car-gallery"
                    rounded
                    @click="toggleMode"
                  >
                    {{ $t('car.view_gallery') }}
                  </Button>
                  <Button
                    v-if="hasVideo"
                    class="car-media__btn button-transparent js-button-car-review"
                    rounded
                    :variant="'outlined'"
                    @click="scrollToVideo"
                  >
                    {{ $t('car.video_car_review') }}
                  </Button>
                </div>
              </div>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
@use 'sass:math';
.car-media {
  display: flex;
  flex-direction: column;
  gap: 80px;
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__title {
    margin: 0;
  }
  &__tabs {
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
    &-headers {
      display: flex;
      justify-content: center;
      position: relative;
      &:deep(.p-tablist-tab-list) {
        justify-content: center;
      }
      &:deep(.p-tablist-active-bar) {
        height: 4px;
      }
      @include media-breakpoint-up(xl) {
        &:deep(.p-tablist-active-bar) {
          @include responsive-spacing(height, 4);
        }
      }
    }
    &-title {
      @include link-normal;
      border-bottom: 1px solid $divider;
      color: $on-surface-variant-2;
      cursor: pointer;
      position: relative;
      padding: 16px;
      text-align: center;
      transition: color 0.2s ease;
      white-space: unset;
      width: auto;
      @include media-breakpoint-up(sm) {
        padding: 17px 16px 15px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-bottom-width, 1);
        @include responsive-spacing(padding-top, 17);
        @include responsive-spacing(padding-right, 16);
        @include responsive-spacing(padding-left, 16);
        @include responsive-spacing(padding-bottom, 15);
      }
      &[data-p='active'] {
        color: $on-surface;
      }
      &--white,
      &--gray {
        color: $on-surface-variant-2;
      }
      &--black {
        color: $on-surface-variant-2;
        &[data-p='active'] {
          color: $white;
        }
      }
    }
  }
  &__bottom {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 32px;
    @include media-breakpoint-up(sm) {
      align-items: center;
      gap: 16px;
      flex-direction: row;
      justify-content: center;
      padding-top: 56px;
      &--gallery {
        padding-top: 0;
      }
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
      @include responsive-spacing(padding-top, 56);
      &--gallery {
        padding-top: 0;
      }
    }
  }
  &__btn {
    @include link-normal;
    margin: 0 auto;
    width: auto;
    @include media-breakpoint-up(sm) {
      margin: 0;
    }
  }
}
</style>
